import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { BrandLogo } from "../components/shared/BrandLogo";
import { RoundLogo } from "../components/shared/RoundLogo";
import { applyPwaBranding } from "../lib/pwaBranding";

export default function Login() {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isDriverLogin = location.pathname.startsWith("/driver");
  const isCustomerLogin = location.pathname.startsWith("/customer");
  const isPosLogin = location.pathname.startsWith("/pos");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Paint the page background brand-green while the (all-green) login screen is
  // mounted so no white shows around it — e.g. the phone's safe-area / overscroll
  // gaps inside the iOS WebView shell.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#1A3626";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  // Installing the driver app from its sign-in screen should name it "The V
  // Delivery" too (iOS reads the title at Add-to-Home-Screen time).
  useEffect(() => {
    if (!isDriverLogin) return undefined;
    return applyPwaBranding({ manifest: "/driver-manifest.json", title: "V Delivery" });
  }, [isDriverLogin]);

  const destFor = (role, u) => {
    if (role === "customer" && u?.must_change_password) return "/customer/set-password";
    if (role === "driver") return "/driver";
    if (role === "customer") return "/customer";
    if (isPosLogin && role === "admin") return "/pos";
    return location.state?.from?.pathname || "/";
  };

  if (!loading && user) {
    return <Navigate to={destFor(user.role, user)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(username, password, rememberMe);
    setSubmitting(false);
    if (!result.success) {
      setError(result.message);
      return;
    }
    navigate(destFor(result.user?.role, result.user), { replace: true });
  };

  return (
    <div
      className="flex min-h-[100dvh] items-center justify-center bg-primary px-4"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center justify-center gap-3">
          {isCustomerLogin && <RoundLogo className="h-20 w-20" />}
          <BrandLogo className="h-14" />
        </div>
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <h1 className="font-heading text-lg font-semibold text-text-main">
              {isDriverLogin ? "Driver sign in" : isCustomerLogin ? "Sign in" : isPosLogin ? "Staff sign in" : "Admin sign in"}
            </h1>
            <p className="mt-1 text-sm text-text-muted">
              {isDriverLogin
                ? "Sign in with your driver account to see today's routes."
                : isCustomerLogin
                ? "Check your plan, QR code, and delivery status."
                : isPosLogin
                ? "Sign in with your staff account to scan pickups."
                : "Operations dashboard for staff."}
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="username"
                  data-testid="login-username-input"
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="login-password-input"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="login-toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(Boolean(v))}
                  data-testid="login-remember-me-checkbox"
                />
                <Label htmlFor="remember-me" className="cursor-pointer text-sm font-normal text-text-muted">
                  Remember me on this device
                </Label>
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger"
                  data-testid="login-error-message"
                >
                  <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                Sign in
              </Button>
            </form>
            {isCustomerLogin && (
              <>
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium uppercase tracking-wide text-text-muted">New here?</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <Button
                  type="button"
                  className="mt-4 w-full"
                  onClick={() => navigate("/customer/register")}
                  data-testid="login-customer-register-link"
                >
                  Register now
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        <p className="mt-6 text-center text-xs text-white/70">
          {isCustomerLogin ? "Having trouble signing in? Contact us for help." : "Staff access only · Contact your administrator for account issues."}
        </p>
      </div>
    </div>
  );
}
